import { useState, useCallback, useEffect } from "react";

// Constants matching native app
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;
export const MIN_FLAT_MEMBERS = 2;
export const MAX_FLAT_MEMBERS = 20;
export const MIN_ROOM_RENT = 0;
export const MAX_ROOM_RENT = 10000;
export const MIN_UTILITIES = 0;
export const MAX_UTILITIES = 2000;
export const MIN_DEPOSIT = 0;
export const MAX_DEPOSIT = 20000;
export const MIN_ROOM_SIZE = 1;
export const MAX_ROOM_SIZE = 200;
export const MIN_FLAT_SIZE = 1;
export const MAX_FLAT_SIZE = 500;
export const MIN_AGE = 18;
export const MAX_AGE = 99;
export const PROFILE_TEXT_MIN_LENGTH = 10;
export const FLAT_TEXT_MIN_LENGTH = 30;
export const PROFILE_TEXT_MAX_LENGTH = 500;
export const MIN_FLAT_ATTRIBUTES = 1;
export const MAX_FLAT_ATTRIBUTES = 20;
export const MIN_IMAGES = 2;

export interface AddressSuggestion {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  currency?: string;
  address?: {
    road?: string;
    village?: string;
    house_number?: string;
    town?: string;
    suburb?: string;
    borough?: string;
    city?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
}

export interface UploadedImage {
  uri: string;
  object_name: string;
  get_url: string;
  progress: number;
}

export type Gender = "male" | "female" | "non_binary";
export type Furnishing = "furnished" | "partly_furnished" | "unfurnished";

export interface CreateFlatState {
  // Step 0: Flat name
  flatName: string;
  // Step 1: Members
  memberCount: string;
  // Step 2: Looking for
  lookingFor: Gender[];
  lookingForAgeRange: [number, number];
  // Step 3: Location
  location: string;
  latitude: string;
  longitude: string;
  street: string;
  // Step 4: Availability
  isPermanent: boolean;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  // Step 5: Pricing
  isSubsidised: boolean;
  rent: string;
  utilities: string;
  deposit: string;
  // Step 6: Room details
  addingFlatSize: boolean;
  flatSize: string;
  roomSize: string;
  equipmentFees: string;
  furnishing: Furnishing;
  // Step 7: Images
  images: UploadedImage[];
  // Step 8: Description
  description: string;
  // Step 9: Attributes
  flatAttributes: string[];
  // Step 10: Question
  question: string;
  // Current step
  step: number;
}

const STORAGE_KEY = "createFlatFormData";

const getInitialState = (): CreateFlatState => {
  // Try to load from localStorage
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert date strings back to Date objects, default fromDate to today
        // Reset step if it's out of bounds (e.g., from old 11-step flow)
        return {
          ...parsed,
          step: parsed.step > 9 ? 0 : parsed.step,
          lookingFor:
            parsed.lookingFor?.length > 0
              ? parsed.lookingFor
              : ["male", "female", "non_binary"],
          fromDate: parsed.fromDate ? new Date(parsed.fromDate) : new Date(),
          toDate: parsed.toDate ? new Date(parsed.toDate) : undefined,
        };
      } catch (e) {
        console.error("Error parsing saved form data:", e);
      }
    }
  }

  return {
    flatName: "",
    memberCount: "",
    lookingFor: ["male", "female", "non_binary"] as Gender[],
    lookingForAgeRange: [MIN_AGE, MAX_AGE],
    location: "",
    latitude: "",
    longitude: "",
    street: "",
    isPermanent: true,
    fromDate: new Date(),
    toDate: undefined,
    isSubsidised: false,
    rent: "",
    utilities: "",
    deposit: "",
    addingFlatSize: false,
    flatSize: "",
    roomSize: "",
    equipmentFees: "",
    furnishing: "unfurnished",
    images: [],
    description: "",
    flatAttributes: [],
    question: "",
    step: 0,
  };
};

export const useCreateFlatStore = () => {
  const [state, setState] = useState<CreateFlatState>(getInitialState);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Setters
  const setFlatName = useCallback((value: string) => {
    setState((s) => ({ ...s, flatName: value }));
  }, []);

  const setMemberCount = useCallback((value: string) => {
    setState((s) => ({ ...s, memberCount: value }));
  }, []);

  const setLookingFor = useCallback((value: Gender[]) => {
    setState((s) => ({ ...s, lookingFor: value }));
  }, []);

  const setLookingForAgeRange = useCallback((value: [number, number]) => {
    setState((s) => ({ ...s, lookingForAgeRange: value }));
  }, []);

  const setLocation = useCallback((value: string) => {
    setState((s) => ({ ...s, location: value }));
  }, []);

  const setLatitude = useCallback((value: string) => {
    setState((s) => ({ ...s, latitude: value }));
  }, []);

  const setLongitude = useCallback((value: string) => {
    setState((s) => ({ ...s, longitude: value }));
  }, []);

  const setStreet = useCallback((value: string) => {
    setState((s) => ({ ...s, street: value }));
  }, []);

  const setIsPermanent = useCallback((value: boolean) => {
    setState((s) => ({ ...s, isPermanent: value }));
  }, []);

  const setFromDate = useCallback((value: Date | undefined) => {
    setState((s) => ({ ...s, fromDate: value }));
  }, []);

  const setToDate = useCallback((value: Date | undefined) => {
    setState((s) => ({ ...s, toDate: value }));
  }, []);

  const setIsSubsidised = useCallback((value: boolean) => {
    setState((s) => ({ ...s, isSubsidised: value }));
  }, []);

  const setRent = useCallback((value: string) => {
    setState((s) => ({ ...s, rent: value }));
  }, []);

  const setUtilities = useCallback((value: string) => {
    setState((s) => ({ ...s, utilities: value }));
  }, []);

  const setDeposit = useCallback((value: string) => {
    setState((s) => ({ ...s, deposit: value }));
  }, []);

  const setAddingFlatSize = useCallback((value: boolean) => {
    setState((s) => ({ ...s, addingFlatSize: value }));
  }, []);

  const setFlatSize = useCallback((value: string) => {
    setState((s) => ({ ...s, flatSize: value }));
  }, []);

  const setRoomSize = useCallback((value: string) => {
    setState((s) => ({ ...s, roomSize: value }));
  }, []);

  const setEquipmentFees = useCallback((value: string) => {
    setState((s) => ({ ...s, equipmentFees: value }));
  }, []);

  const setFurnishing = useCallback((value: Furnishing) => {
    setState((s) => ({ ...s, furnishing: value }));
  }, []);

  const setImages = useCallback(
    (value: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])) => {
      setState((s) => ({
        ...s,
        images: typeof value === "function" ? value(s.images) : value,
      }));
    },
    [],
  );

  const addImage = useCallback((image: UploadedImage) => {
    setState((s) => ({ ...s, images: [...s.images, image] }));
  }, []);

  const removeImage = useCallback((index: number) => {
    setState((s) => ({
      ...s,
      images: s.images.filter((_, i) => i !== index),
    }));
  }, []);

  const updateImageProgress = useCallback((index: number, progress: number) => {
    setState((s) => ({
      ...s,
      images: s.images.map((img, i) =>
        i === index ? { ...img, progress } : img,
      ),
    }));
  }, []);

  const setDescription = useCallback((value: string) => {
    setState((s) => ({ ...s, description: value }));
  }, []);

  const setFlatAttributes = useCallback((value: string[]) => {
    setState((s) => ({ ...s, flatAttributes: value }));
  }, []);

  const toggleAttribute = useCallback((attributeId: string) => {
    setState((s) => ({
      ...s,
      flatAttributes: s.flatAttributes.includes(attributeId)
        ? s.flatAttributes.filter((id) => id !== attributeId)
        : [...s.flatAttributes, attributeId],
    }));
  }, []);

  const setQuestion = useCallback((value: string) => {
    setState((s) => ({ ...s, question: value }));
  }, []);

  const setStep = useCallback((value: number) => {
    setState((s) => ({ ...s, step: value }));
  }, []);

  const nextStep = useCallback(() => {
    setState((s) => ({ ...s, step: s.step + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setState((s) => ({ ...s, step: Math.max(0, s.step - 1) }));
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      flatName: "",
      memberCount: "",
      lookingFor: ["male", "female", "non_binary"],
      lookingForAgeRange: [MIN_AGE, MAX_AGE],
      location: "",
      latitude: "",
      longitude: "",
      street: "",
      isPermanent: true,
      fromDate: undefined,
      toDate: undefined,
      isSubsidised: false,
      rent: "",
      utilities: "",
      deposit: "",
      addingFlatSize: false,
      flatSize: "",
      roomSize: "",
      equipmentFees: "",
      furnishing: "unfurnished",
      images: [],
      description: "",
      flatAttributes: [],
      question: "",
      step: 0,
    });
  }, []);

  // Validation helpers
  const numberCheck = useCallback(
    (num: string, min?: number, max?: number): boolean => {
      const parsed = parseFloat(num);
      return (
        !isNaN(parsed) &&
        parsed >= 0 &&
        (!max || parsed <= max) &&
        (!min || parsed >= min)
      );
    },
    [],
  );

  const stringCheck = useCallback(
    (str: string, min?: number, max?: number): boolean => {
      return (
        !!str?.trim() &&
        (!max || str.trim().length <= max) &&
        (!min || str.trim().length >= min)
      );
    },
    [],
  );

  // Step validation
  const isStepValid = useCallback(
    (stepIndex: number): boolean => {
      switch (stepIndex) {
        case 0: // Flat name
          return stringCheck(
            state.flatName,
            USERNAME_MIN_LENGTH,
            USERNAME_MAX_LENGTH,
          );
        case 1: // Members + Looking for
          return (
            numberCheck(
              state.memberCount,
              MIN_FLAT_MEMBERS,
              MAX_FLAT_MEMBERS,
            ) &&
            state.lookingFor.length > 0 &&
            state.lookingForAgeRange[0] >= MIN_AGE &&
            state.lookingForAgeRange[0] <= MAX_AGE &&
            state.lookingForAgeRange[1] >= MIN_AGE &&
            state.lookingForAgeRange[1] <= MAX_AGE &&
            state.lookingForAgeRange[0] <= state.lookingForAgeRange[1]
          );
        case 2: // Location
          return !!state.latitude && !!state.longitude;
        case 3: // Availability
          return (
            state.fromDate !== undefined &&
            (state.isPermanent ||
              (state.toDate !== undefined && state.fromDate < state.toDate))
          );
        case 4: // Pricing
          return (
            numberCheck(state.rent, MIN_ROOM_RENT, MAX_ROOM_RENT) &&
            numberCheck(state.utilities, MIN_UTILITIES, MAX_UTILITIES) &&
            numberCheck(state.deposit, MIN_DEPOSIT, MAX_DEPOSIT)
          );
        case 5: // Room details
          return (
            ((numberCheck(state.flatSize, MIN_FLAT_SIZE, MAX_FLAT_SIZE) &&
              parseFloat(state.flatSize) >= parseFloat(state.roomSize)) ||
              !state.addingFlatSize) &&
            numberCheck(state.roomSize, MIN_ROOM_SIZE, MAX_ROOM_SIZE) &&
            (numberCheck(state.equipmentFees) ||
              state.furnishing === "unfurnished")
          );
        case 6: // Images
          return (
            state.images.length >= MIN_IMAGES &&
            state.images.every((img) => img.progress === 1)
          );
        case 7: // Description
          return stringCheck(
            state.description,
            FLAT_TEXT_MIN_LENGTH,
            PROFILE_TEXT_MAX_LENGTH,
          );
        case 8: // Attributes
          return (
            state.flatAttributes.length >= MIN_FLAT_ATTRIBUTES &&
            state.flatAttributes.length <= MAX_FLAT_ATTRIBUTES
          );
        case 9: // Question (optional)
          return true;
        default:
          return true;
      }
    },
    [state, numberCheck, stringCheck],
  );

  // Get currency from location
  const getCurrency = useCallback((): string => {
    if (!state.location) return "CHF";
    try {
      const parsed = JSON.parse(state.location);
      return parsed.currency || "CHF";
    } catch {
      return "CHF";
    }
  }, [state.location]);

  return {
    ...state,
    setFlatName,
    setMemberCount,
    setLookingFor,
    setLookingForAgeRange,
    setLocation,
    setLatitude,
    setLongitude,
    setStreet,
    setIsPermanent,
    setFromDate,
    setToDate,
    setIsSubsidised,
    setRent,
    setUtilities,
    setDeposit,
    setAddingFlatSize,
    setFlatSize,
    setRoomSize,
    setEquipmentFees,
    setFurnishing,
    setImages,
    addImage,
    removeImage,
    updateImageProgress,
    setDescription,
    setFlatAttributes,
    toggleAttribute,
    setQuestion,
    setStep,
    nextStep,
    prevStep,
    reset,
    isStepValid,
    getCurrency,
  };
};

export type CreateFlatStore = ReturnType<typeof useCreateFlatStore>;
